import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import {
  assignMySelf,
  deleteGame,
  mockResult,
  removeScorer,
  resetGame
} from '../../../features/game/gameSlice';
import EditGame from '../../games/editGame';
import { useAuth } from '../../../contexts/authContext';
import { hasWorkspaceAccess, Roles } from '../../../helpers/access';
import AssignScorer from '../../games/assignScorer';

import PictureIcon from '@mui/icons-material/PhotoCamera';
import { ScorecardPhotoDialog } from '../gameControls/viewScorecardPhoto';
import gameService from '../../../features/game/gameService';

import TrashIcon from '@mui/icons-material/Close';
import { getVenueById } from '../../../features/venue/venueSlice';
import { green } from '@mui/material/colors';

const GameCardBase = ({ children, game, actions, headerAction, portal }) => {
  const dispatch = useDispatch();
  const deleting = useSelector((state) => state.game.deleting);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [assignSelfOpen, setAssignSelfOpen] = useState(false);
  const [openReset, setOpenReset] = useState(false);
  const [mockingResult, setMockingResult] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoSrc, setPhotoSrc] = useState('');
  const [photoStatus, setPhotoStatus] = useState('idle');
  const photoRequestRef = useRef(0);
  const [gameScorerName, setGameScorerName] = useState(
    `${game.scorer?.firstName} ${game.scorer?.lastName}`
  );

  const { authenticated, currentUserProfile, currentUser, currentWorkspace } = useAuth();
  const role = currentUserProfile?.role;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (game.scorer && (!game.scorer.firstName || game.scorer.firstName === '')) {
      //console.log(game.scorer);
      //Get Scorer from Id.
    }
  }, [game.scorer]);

  const handleEditClose = () => {
    setOpenEdit(false);
  };

  const handleEdit = () => {
    setOpenEdit(true);
  };

  const handleDelete = (game) => {
    setSelectedGame(game);
    setOpenDelete(true);
  };

  const handleClose = () => {
    setSelectedGame(null);
    setOpenDelete(false);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteGame(selectedGame.id))
      .unwrap()
      .then(() => {
        setSelectedGame(null);
        setOpenDelete(false);
      });
  };

  const handleAssignSelfOpen = () => {
    setAssignSelfOpen(true);
  };

  const handleAssignSelfClose = () => {
    setAssignSelfOpen(false);
  };

  const handleConfirmAssignSelf = () => {
    dispatch(assignMySelf({ gameId: game.id }))
      .unwrap()
      .then(() => {
        setAssignSelfOpen(false);
      });
  };

  const handleRemoveScorer = () => {
    dispatch(removeScorer({ gameId: game.id }));
  };

  const handleConfirmReset = () => {
    dispatch(resetGame(game.id))
      .unwrap()
      .then(() => {
        setOpenReset(false);
      });
  };

  const handleMockResult = () => {
    setMockingResult(true);
    dispatch(mockResult(game.id))
      .unwrap()
      .finally(() => {
        setMockingResult(false);
      });
  };

  // List cards only carry the photo flag, so open the viewer immediately and
  // fetch the one game that has the photo. The fetched copy is dropped when
  // the viewer closes so the base64 string does not stay on the card.
  const openPhotoViewer = (event) => {
    event?.stopPropagation();

    const requestId = photoRequestRef.current + 1;
    photoRequestRef.current = requestId;

    setPhotoOpen(true);
    setPhotoSrc('');

    if (game.scorecardPhoto) {
      setPhotoSrc(game.scorecardPhoto);
      setPhotoStatus('ready');
      return;
    }

    if (!game.hasScorecardPhoto) {
      setPhotoStatus('unavailable');
      return;
    }

    setPhotoStatus('loading');

    const load = portal?.loadPhoto
      ? Promise.resolve().then(() => portal.loadPhoto())
      : gameService.getById(game.id);

    load
      .then((result) => {
        if (photoRequestRef.current !== requestId) return;
        const photo =
          typeof result === 'string'
            ? result
            : result?.scorecardPhoto || result?.game?.scorecardPhoto || '';
        setPhotoSrc(photo);
        setPhotoStatus(photo ? 'ready' : 'unavailable');
      })
      .catch(() => {
        if (photoRequestRef.current !== requestId) return;
        setPhotoStatus('unavailable');
      });
  };

  const closePhotoViewer = () => {
    photoRequestRef.current += 1;
    setPhotoOpen(false);
    setPhotoSrc('');
    setPhotoStatus('idle');
  };

  const setBorder = () => {
    if (game.live) {
      return { border: 'solid 2px', borderColor: 'red.main' };
    } else if (game.featured) {
      return { border: 'solid 2px', borderColor: 'yellow.main' };
    } else {
      return null;
    }
  };

  const setTag = () => {
    if (game.live) {
      return (
        <Box
          position="absolute"
          top={0}
          right={0}
          backgroundColor="red.main"
          p={'5px'}
          fontSize={10}
          color={'background.main'}
          display="flex"
          sx={{ borderRadius: '0 8px 0 8px' }}>
          {game.live && 'Live'}
        </Box>
      );
    } else if (game.featured) {
      return (
        <Box
          position="absolute"
          top={0}
          right={0}
          backgroundColor="yellow.main"
          p={'5px'}
          fontSize={10}
          color={'background.main'}
          display="flex"
          sx={{ borderRadius: '0 8px 0 8px' }}>
          {game.featured && 'Featured'}
        </Box>
      );
    } else {
      return null;
    }
  };

  return (
    <Paper
      sx={{
        width: '100%',
        maxWidth: portal?.cardMaxWidth || 450,
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        ...setBorder(),
        position: 'relative'
      }}
      elevation={3}>
      {setTag()}
      <Stack justifyContent={'space-between'} height="100%">
        <Box
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          width={'100%'}
          gap={1}
          minWidth={0}>
          <Typography
            textAlign={'center'}
            sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {game.name ? game.name : 'Private Game'}
          </Typography>

          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {headerAction}

            {!portal &&
              actions &&
              hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) && (
              <>
                <IconButton size="small" color="error" onClick={() => handleDelete(game)}>
                  <Delete />
                </IconButton>
                <IconButton size="small" color="info" onClick={() => handleEdit(game)}>
                  <Edit />
                </IconButton>
              </>
            )}

            {(game.scorecardPhoto || game.hasScorecardPhoto) && (
              <IconButton
                size="small"
                color="success"
                aria-label="View scorecard photo"
                onClick={openPhotoViewer}>
                <PictureIcon />
              </IconButton>
            )}
          </Box>
        </Box>
        {/* <Typography variant={'caption'} p={0} m={0}>
          Current End: {game.currentEnd} / {game.gameFormat.ends}
        </Typography> */}

        <EditGame game={game} open={openEdit} onClose={handleEditClose} />

        {children}

        {game.venue && (
          <Typography
            variant="caption"
            sx={{
              alignSelf: 'flex-end',
              mt: 2,
              maxWidth: '100%',
              textAlign: 'right',
              overflowWrap: 'anywhere'
            }}>
            {typeof game.venue === 'string' ? game.venue : game.venue.name} - {game.green}
            {game.rink}
          </Typography>
        )}

        {game.updatedAt &&
          (() => {
            const raw = game.updatedAt;
            let date;
            if (typeof raw?.toDate === 'function') date = raw.toDate();
            else if (typeof raw?.seconds === 'number') date = new Date(raw.seconds * 1000);
            else if (typeof raw?._seconds === 'number') date = new Date(raw._seconds * 1000);
            else date = new Date(raw);

            const isValid = date instanceof Date && !Number.isNaN(date.getTime());
            return isValid ? (
              <Typography variant="caption" sx={{ alignSelf: 'flex-end' }} color="grey.700">
                {date.toLocaleDateString()}
              </Typography>
            ) : null;
          })()}

        {!portal && hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.SUPERUSER) && (
          <Typography variant="caption" sx={{ alignSelf: 'flex-end' }}>
            Game ID: {game.id}
          </Typography>
        )}

        {!portal &&
          actions &&
          hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) && (
          <Button
            variant="text"
            color="warning"
            size="small"
            sx={{ alignSelf: 'flex-end', fontSize: '0.75rem', p: 0 }}
            onClick={() => setOpenReset(true)}>
            Reset Game
          </Button>
        )}

        {!portal &&
          actions &&
          hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.SUPERUSER) && (
          <Button
            variant="text"
            color="info"
            size="small"
            sx={{ alignSelf: 'flex-end', fontSize: '0.75rem', p: 0 }}
            disabled={mockingResult}
            onClick={handleMockResult}
            startIcon={mockingResult ? <CircularProgress size={12} color="inherit" /> : null}>
            {mockingResult ? 'Generating...' : 'Mock Result'}
          </Button>
        )}

        {/* {actions && hasAccess(currentUserProfile, Roles.ADMIN) ? (
          game.scorer ? (
            <Box display="flex" justifyContent={'flex-end'} alignItems={'center'} gap={1}>
              <Typography variant="caption">
                Scorer: {game.scorer.firstName} {game.scorer.lastName}
              </Typography>
              <AssignScorer game={game} scorer={game.scorer} />
            </Box>
          ) : (
            <>
              <AssignScorer game={game} />
              <Box display="flex" justifyContent={'flex-end'} alignItems={'center'}>
                <Button
                  onClick={handleAssignSelfOpen}
                  color="info"
                  size={'small'}
                  sx={{ fontSize: '0.8rem', py: 0, px: '2px' }}>
                  Assign Myself
                </Button>
              </Box>
            </>
          )
        ) : (
          authenticated && (
            <Box display="flex" justifyContent={'flex-end'} alignItems={'center'} gap={1} mt={2}>
              {game.scorer && (
                <>
                  <Typography variant="caption">
                    Scorer: {game.scorer.firstName} {game.scorer.lastName}
                  </Typography>

                  {(hasAccess(currentUserProfile, Roles.SCORER) ||
                    currentUser.uid === game.scorer.id) && (
                    <Tooltip title="Remove Scorer">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveScorer(game.scorer)}>
                        <TrashIcon fontSize="0.8rem" />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}

              {hasAccess(currentUserProfile, Roles.SCORER) && (
                <Box display="flex" justifyContent={'flex-end'} alignItems={'center'}>
                  <Button
                    onClick={handleAssignSelfOpen}
                    color="success"
                    size={'small'}
                    sx={{ fontSize: '0.8rem', py: 0, px: '2px' }}>
                    Assign Myself
                  </Button>
                </Box>
              )}
            </Box>
          )
        )} */}
      </Stack>

      {selectedGame && (
        <Dialog
          open={openDelete}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">Delete Game</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete the game:{' '}
              {selectedGame.name ? selectedGame.name : 'Private Game'}? It will be hidden from the
              event and can be brought back by the assistant.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            {deleting ? (
              <CircularProgress />
            ) : (
              <>
                <Button onClick={handleClose} color="primary">
                  Cancel
                </Button>
                <Button onClick={handleConfirmDelete} color="primary" autoFocus>
                  Confirm
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={openReset}
        onClose={() => setOpenReset(false)}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description">
        <DialogTitle id="reset-dialog-title">Reset Game</DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-dialog-description">
            Are you sure you want to reset the game: {game.name ? game.name : 'Private Game'}? This
            will clear all scores and return it to Not Started.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReset(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmReset} color="warning" autoFocus>
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignSelfOpen}
        onClose={handleAssignSelfClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">Assign Myself</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to assign yourself as the scorer for the game:{' '}
            {game.name ? game.name : 'Private Game'}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignSelfClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmAssignSelf} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <ScorecardPhotoDialog
        open={photoOpen}
        onClose={closePhotoViewer}
        photoSrc={photoSrc}
        title={`Scorecard for ${game.name || 'Private Game'}`}
        loading={photoStatus === 'loading'}
        error={
          photoStatus === 'unavailable' ? 'This scorecard photo could not be loaded.' : ''
        }
      />
    </Paper>
  );
};

export default GameCardBase;
